import { useContext, useEffect, useState, type CSSProperties } from 'react';
import { Dropdown } from 'antd';
import { InputTypeSelect } from './styles';
import { TeamsMenuUnassignPlaylist } from 'components/teamSelect';
import TeamAssignmentDeadlineMenu from 'components/teamSelect/TeamAssignmentDeadlineMenu';
import { SessionContext } from 'contexts/session';
import { Team } from 'contexts/teams/types';
import Icon from 'components/icons';
import { Spinner } from 'components/loadings';
import Tooltip from 'components/tooltip';
import { PlaylistUnassignTeamSelectorProps } from './types';

const PlaylistUnassignTeamSelector = ({
  teamsSelected,
  disabled,
  disabledTooltip,
  onUnassignSubmit,
  wrapperStyles,
  containerStyles,
  bgColor,
  wideInput,
  teamDeadlineRows,
  playlistDurationSeconds,
  loadingTeamId,
  onManageDeadline,
  onRemoveAssignment,
}: PlaylistUnassignTeamSelectorProps): JSX.Element => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { account, teams, isTeamManager } = useContext(SessionContext);

  useEffect(() => {
    if (disabled) setMenuVisible(false);
  }, [disabled]);

  const oneTeamManager = isTeamManager && teams && teams.length === 1;
  // The single-team lockout only makes sense for the legacy unassign flow (there's nothing to
  // "select" with one team). The team-deadline dropdown still needs to open so a single-team
  // Team Manager can manage/remove that team's deadline.
  const isSingleTeamManagerLockout = oneTeamManager && !teamDeadlineRows;

  const handleManageDeadline = (teamId: string) => {
    setMenuVisible(false);
    onManageDeadline?.(teamId);
  };

  const handleRemoveAssignment = (teamId: string) => {
    setMenuVisible(false);
    onRemoveAssignment?.(teamId);
  };

  const handleUnassignSubmit = async (teamIds: string[]) => {
    setIsSubmitting(true);
    const newTeams: any[] = [];
    teamIds.map((teamId) => {
      const newTeam = teams?.filter(({ id }) => id === teamId)?.[0];
      newTeams.push({
        id: teamId,
        name: newTeam?.name,
        icon: newTeam?.icon || null,
        slug: newTeam?.slug,
      });
    });
    try {
      await Promise.resolve(onUnassignSubmit(newTeams));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!teams) return null;

  const currentTeamsSelected = oneTeamManager
    ? [
        {
          team: teams[0],
          team_id: teams[0].id,
          name: teams[0].name,
          is_manager: isTeamManager,
        },
      ]
    : teamsSelected;

  const teamsForMenu = currentTeamsSelected
    .map((team) => team.team)
    .filter((team): team is Team => Boolean(team));

  const stylesProp: CSSProperties = { ...(containerStyles ?? {}), position: 'relative' };
  const isTriggerDisabled = disabled || isSingleTeamManagerLockout || isSubmitting;

  return (
    <div style={{ ...wrapperStyles }}>
      <div style={{ ...stylesProp }}>
        <Dropdown
          overlay={
            teamDeadlineRows ? (
              <TeamAssignmentDeadlineMenu
                teams={teamDeadlineRows}
                playlistDurationSeconds={playlistDurationSeconds}
                loadingTeamId={loadingTeamId}
                onManageDeadline={handleManageDeadline}
                onRemoveAssignment={handleRemoveAssignment}
              />
            ) : (
              <TeamsMenuUnassignPlaylist
                accountTeams={teamsForMenu}
                onClose={() => setMenuVisible(false)}
                onSubmit={handleUnassignSubmit}
                withSubmit={true}
                withUsersCount
                account={account}
                full
                teamsSelected={currentTeamsSelected}
              />
            )
          }
          trigger={['click']}
          visible={menuVisible && !isSingleTeamManagerLockout}
          onVisibleChange={setMenuVisible}
          disabled={isTriggerDisabled}
          placement={'bottomRight'}
        >
          <Tooltip
            destroyTooltipOnHide
            placement="top"
            overlayInnerStyle={{ whiteSpace: 'pre-line', textAlign: 'center' }}
            title={isTriggerDisabled && disabledTooltip ? disabledTooltip : null}
          >
            <InputTypeSelect
              onClick={() => {
                if (isTriggerDisabled) return;
                setMenuVisible((prevState) => !prevState);
              }}
              bgColor={bgColor}
              wideInput={wideInput}
              style={{
                cursor: isTriggerDisabled ? 'not-allowed' : 'pointer',
                background: isTriggerDisabled ? 'var(--grey-lighter)' : bgColor,
                opacity: isTriggerDisabled ? 0.7 : 1,
                color: 'var(--light-gray-darker)',
              }}
            >
              <p style={{ fontSize: '15px' }}>{`Teams assigned: ${currentTeamsSelected.length}`}</p>
              {!isSingleTeamManagerLockout && <Icon icon={'ChevronDown'} size={'smaller'} />}
            </InputTypeSelect>
          </Tooltip>
        </Dropdown>
        <Spinner
          active={isSubmitting}
          position="absolute"
          top="50%"
          left="50%"
          size="20"
          zIndex="20"
        />
      </div>
    </div>
  );
};

export default PlaylistUnassignTeamSelector;
